import React, { useEffect, useRef, useState } from "react";
import FlightCard from "./Components/flightCard";
import FareCard from "./Components/fareCard";
import Sort from "./Components/sort";
import { classifyTime } from "../../../../../../../Utils/utils";
import { useUserPolicy } from "../../../../../../../Hooks/useUserPolicy";
import UserChoice from "./Components/userChoice";
import HasApproval from "./Components/hasApproval";
import { apis, post } from "Utils/axios";
import { getApprovedFlightMatchIndex } from "Utils/flightApproval";
import { Button, Toast } from "Components";
import { FaArrowDown } from "react-icons/fa";
import { FiFilter } from "react-icons/fi";
import moment from "moment";
import { useUserConfig } from "Hooks/useUserConfig";

function FlightResult({ approval, filters = {}, onSelect = () => {}, SelectedFlight, flights = [], fromCode, toCode, date, adult, child, infant, cabinClass, requestId, approvalId, isMobile = false, onOpenFilters = () => {} }) {
	const { travelPolicy } = useUserPolicy();
	const { checkUserPermission } = useUserConfig();
	const [Filters, setFilters] = useState(filters);
	const [SortBy, setSortBy] = useState({ type: "price", order: "asc" });
	const { showOutOfPolicyOptions = true } = travelPolicy?.domesticFlight || {};
	const [LoadingFamilyFares, setLoadingFamilyFares] = useState(false);
	const [FamilyFares, setFamilyFares] = useState({});

	const PAGE_LIMIT = 50;
	const [Chunk, setChunk] = useState(PAGE_LIMIT);

	useEffect(() => {
		setFilters(filters);
		setChunk(PAGE_LIMIT);
	}, [filters, SortBy]);

	const loadNextChunk = (prev) => {
		if (prev === flights.length) {
			return;
		}
		if (prev + PAGE_LIMIT > flights.length) {
			setChunk(flights.length);
			return;
		}
		setChunk(prev + PAGE_LIMIT);
	};

	const targetRef = useRef(null);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) loadNextChunk(Chunk);
			},
			{ root: null, rootMargin: "0px", threshold: 0.2 }
		);

		const currentTarget = targetRef.current;

		if (currentTarget) {
			observer.observe(currentTarget);
		}

		return () => {
			if (currentTarget) {
				observer.unobserve(currentTarget);
			}
		};
	}, [Chunk]);

	const getFamilyFare = async (sid, fare) => {
		setLoadingFamilyFares(fare.fareKey);
		post(
			apis.getFamilyFare,
			{
				from: fromCode,
				to: toCode,
				date: moment(date).format("DD-MMM-YYYY"),
				adult: String(adult),
				child: String(child),
				infant: String(infant),
				cabinClass,
				requestId,
				approvalId,
				flightOptionKey: fare.fareKey,
				flightSearchKey: sid,
			},
			(r, e) => {
				setLoadingFamilyFares(false);
				if (r && r.length > 0) {
					//Cache the family fares for the fare key
					setFamilyFares((prev) => ({ ...prev, [fare.fareKey]: r.map((fare) => ({ ...fare, isFamilyFare: true })) }));
				} else {
					setFamilyFares((prev) => ({ ...prev, [fare.fareKey]: [fare] }));
				}
			}
		);
	};

	const handleSelect = (selectedFare, flight) => {
		console.log(selectedFare, flight);
		if (selectedFare) {
			onSelect({ ...flight, selectedFare });
		} else {
			onSelect();
		}
	};

	const sortFlights = (prev, next) => {
		const prevSegments = prev?.segments || prev?.onwardSegments || [];
		const nextSegments = next?.segments || next?.onwardSegments || [];

		if (SortBy.type === "price") {
			//For family fares, compare using family fares
			const prevPrice = prev.familyFares?.length > 0 ? prev.familyFares[0].price : prev.fares[0].price;
			const nextPrice = next.familyFares?.length > 0 ? next.familyFares[0].price : next.fares[0].price;
			return SortBy.order === "des" ? nextPrice - prevPrice : prevPrice - nextPrice;
		} else if (SortBy.type === "departure") {
			const prevDeparture = prevSegments[0]?.departureTime;
			const nextDeparture = nextSegments[0]?.departureTime;
			return SortBy.order === "des" ? moment.utc(nextDeparture).diff(moment.utc(prevDeparture)) : moment.utc(prevDeparture).diff(moment.utc(nextDeparture));
		} else if (SortBy.type === "arrival") {
			const prevArrival = prevSegments[prevSegments.length - 1]?.arrivalTime;
			const nextArrival = nextSegments[nextSegments.length - 1]?.arrivalTime;
			return SortBy.order === "des" ? moment.utc(nextArrival).diff(moment.utc(prevArrival)) : moment.utc(prevArrival).diff(moment.utc(nextArrival));
		} else if (SortBy.type === "duration") {
			return SortBy.order === "des" ? moment.utc(next.duration).diff(moment.utc(prev.duration)) : moment.utc(prev.duration).diff(moment.utc(next.duration));
		}
	};

	const filterFlights = (flight) => {
		if (!flight.fares.length) {
			return false;
		}
		const _stops = flight.segments.length - 1;
		const _inPolicy = flight.inPolicy;
		const _fullyRefundable = flight.fares[0].refundable;
		const _airline = flight.segments[0].airlineName;
		const _time = classifyTime(flight.segments[0].departureTime);
		const lastSegment = flight.segments[flight.segments.length - 1];
		const _arrivalTime = lastSegment?.arrivalTime != null ? classifyTime(lastSegment.arrivalTime) : undefined;
		const _price = flight.fares[0].price;

		const { maxPrice, airlines, stops, time, arrivalTime = [], inPolicy, fullyRefundable } = Filters;

		let show = true;

		if (maxPrice && _price - 1 > maxPrice) {
			show = false;
		}
		if (stops.length && !stops.includes(_stops)) {
			show = false;
		}
		if (inPolicy && !_inPolicy) {
			show = false;
		}
		if (fullyRefundable && !_fullyRefundable) {
			show = false;
		}
		if (airlines.length && !airlines.includes(_airline)) {
			show = false;
		}
		if (time.length && !time.includes(_time)) {
			show = false;
		}
		if (arrivalTime.length && (_arrivalTime === undefined || !arrivalTime.includes(_arrivalTime))) {
			show = false;
		}

		//Check for TA permission
		const hasFares = flight.fares.some((fare) => fare.price > 0);

		if (!hasFares || (!flight.inPolicy && !approval && showOutOfPolicyOptions === false && !checkUserPermission("TA"))) {
			show = false;
		}

		return show;
	};

	const filteredFlights = flights.filter(filterFlights);
	const sortedFlights = [...filteredFlights].sort(sortFlights);
	const approvedFlightIndex = getApprovedFlightMatchIndex({ approval, flights: sortedFlights });
	const orderedFlights =
		approvedFlightIndex >= 0
			? [sortedFlights[approvedFlightIndex], ...sortedFlights.filter((_, index) => index !== approvedFlightIndex)]
			: sortedFlights;

	if (!orderedFlights.length) {
		return (
			<div className="flex-col w-full py-20 flex-center">
				<h1 className="mb-12 text-2xl text-center">No Flights found</h1>
			</div>
		);
	}

	return (
		<>
			<div className="sticky top-0 z-10 bg-white min-w-0 overflow-visible">
				<UserChoice />
				<HasApproval approval={approval} />
				<div className="my-3 rounded-lg border border-surface bg-canvas/95 backdrop-blur px-2 py-2 sm:px-3">
					<div className="flex items-center gap-2">
						<div className="flex-1 min-w-0">
							<Sort sortBy={SortBy} updateSort={setSortBy} />
						</div>
						<Button onClick={onOpenFilters} variant="outlined" className="btn-xs shrink-0">
							<FiFilter className="mr-1" />
							Filter
						</Button>
					</div>
				</div>
			</div>
			{orderedFlights
				.slice(0, Chunk)
				.map((flight, index) => {
					const isApprovedChoice = approvedFlightIndex >= 0 && index === 0;
					const { segments, fares, duration, familyFares, familyFare, vendor } = flight;
					const cheapestFare = fares.reduce((prev, current) => (prev.price < current.price ? prev : current));
					const highestFare = fares.reduce((prev, current) => (prev.price > current.price ? prev : current));
					let loadingAmadeusFares = LoadingFamilyFares === fares[0].fareKey;

					return (
						<React.Fragment key={`${segments?.[0]?.airlineCode || "flight"}-${segments?.[0]?.flightNumber || index}-${index}`}>
							{approvedFlightIndex >= 0 && index === 1 && (
								<div className="mt-6 mb-2 px-1">
									<div className="text-sm font-semibold text-dark">Other eligible options</div>
									<div className="mt-1 text-xs text-secondary">You can compare the approved flight with other fares that fit within your approval limit.</div>
								</div>
							)}
							<div className={`mt-4 border rounded-md shadow-sm bg-canvas overflow-hidden ${isApprovedChoice ? "border-successText ring-2 ring-successText/15 shadow-md" : ""}`}>
							{isApprovedChoice && (
								<div className="border-b border-successText/20 bg-success px-4 py-3">
									<div className="flex items-center justify-between gap-3">
										<div className="text-sm font-semibold text-successText">Recommended approved flight</div>
										<div className="rounded-full border border-successText/15 bg-successText px-2 py-1 text-[11px] font-medium text-white">Matches approval</div>
									</div>
									<div className="mt-1 text-xs text-strong">This is the flight approved for the request. Review this first, then compare alternatives below if needed.</div>
								</div>
							)}
							<div key={index} className="flex flex-col min-w-0 lg:flex-row">
								<FlightCard key={index} segments={segments} duration={duration} />
								<div className="flex items-stretch flex-1 overflow-x-auto border-t border-l-0 lg:border-t-0 lg:border-l divide-x min-w-0">
									{/* {(familyFare ? familyFares : fares).map((fare, i) => {
										return <FareCard loadFamilyFare={loadFamilyFare} approval={approval} key={i} selected={SelectedFlight} onSelect={(selectedFare) => handleSelect(selectedFare, flight)} {...{ flight, fare, cheapestFare, highestFare, familyFare }} />;
									})} */}
									{familyFare ? (
										//Family Fare
										vendor.toLowerCase() === "verteil" ? (
											//Verteil
											familyFares.map((fare, i) => <FareCard approval={approval} key={i} selected={SelectedFlight} onSelect={(selectedFare) => handleSelect(selectedFare, flight)} {...{ flight, fare, cheapestFare, highestFare, familyFare }} />)
										) : //Amadeus
										vendor.toLowerCase() === "amadeus" ? (
											FamilyFares[fares[0].fareKey] ? (
												FamilyFares[fares[0].fareKey].map((fare, i) => <FareCard approval={approval} key={i} selected={SelectedFlight} onSelect={(selectedFare) => handleSelect(selectedFare, flight)} {...{ flight, fare, cheapestFare, highestFare, familyFare }} />)
											) : (
												<div className="flex flex-col justify-between flex-1 h-full gap-4 p-5 m-auto text-center flex-center">
												<div>
													<p className="text-sm font-bold text-nowrap">✈️ Multiple fare options available</p>
													<p className="mt-1 text-xs text-secondary">Open options, then select one fare to continue</p>
												</div>
												<Button loading={loadingAmadeusFares} onClick={() => getFamilyFare(flight.sid, fares[0])} variant="primary" className="btn-sm">
													View Fare Options
													</Button>
												</div>
											)
										) : null
									) : (
										fares.map((fare, i) => <FareCard approval={approval} key={i} selected={SelectedFlight} onSelect={(selectedFare) => handleSelect(selectedFare, flight)} {...{ flight, fare, cheapestFare, highestFare, familyFare }} />)
									)}
								</div>
							</div>
							</div>
						</React.Fragment>
					);
				})}
			<div ref={targetRef} className="p-4 my-4 flex-center">
				{Chunk < orderedFlights.length && (
					<Button onClick={() => loadNextChunk(Chunk)} className="gap-2">
						<FaArrowDown />
						Load More
					</Button>
				)}
			</div>
		</>
	);
}

export default FlightResult;
